import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as moment from 'moment';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { CalendarSmartService } from 'src/app/_services/calendar-smart.service';
import { TeamsService } from 'src/app/_services/teams.service';
import { BaseComponent } from '../../base/base.component';
import { ModalDesiredSmartComponent } from '../modal-desired-smart/modal-desired-smart.component';

@Component({
  selector: 'app-sidebar-calendar',
  templateUrl: './sidebar-calendar.component.html',
  styleUrls: ['./sidebar-calendar.component.scss'],
  animations: [
    trigger('slideUp', [
      state('open', style({ transform: 'translateY(0%)' })),
      state('closed', style({ transform: 'translateY(100%)' })),
      transition('open => closed', [animate('300ms ease-in')]),
      transition('closed => open', [animate('300ms ease-out')])
    ])
  ]
})
export class SidebarCalendarComponent extends BaseComponent {
  // import dei service
  private readonly _teamService: TeamsService = inject(TeamsService);
  private readonly _modalService: NzModalService = inject(NzModalService);
  private readonly _calendarSmartService: CalendarSmartService =
    inject(CalendarSmartService);

  @Input() profileData: any;
  @Input() userRole: string = '';
  @Input() isSideOpen: boolean = false;
  @Input() widthColumn: any;
  @Input() monthSelected: Date = new Date();
  @Input() notifications: any = null;
  @Output() closed = new EventEmitter<void>();
  @Output() teamsEmitter = new EventEmitter<number[]>();
  @Output() rolesEmitter = new EventEmitter<number[]>();
  @Output() eventsEmitter = new EventEmitter<string[]>();
  @Output() dateEmitter = new EventEmitter<{
    startWeek: Date;
    endWeek: Date;
  }>();
  @Output() filterUsersrequest = new EventEmitter<{
    usersId: string[];
    startWeek: Date;
  }>();
  @Output() onPageChangeCalendar = new EventEmitter<Date>();
  @ViewChild('datePicker', { read: ElementRef }) datePicker!: ElementRef;

  animationState: 'open' | 'closed' = 'open';
  first: boolean = true;
  teams: any[] = [];
  teamsSelected: number[] = [];
  roles: any[] = [
    { label: 'Sviluppatore', value: 1 },
    { label: 'Sistemista', value: 2 },
    { label: 'Designer', value: 3 },
    { label: 'Scrum Master', value: 4 },
    { label: 'Team Leader', value: 5 },
    { label: 'Project Manager', value: 6 }
  ];
  rolesSelected: number[] = [];
  events: any[] = [
    {
      label: 'Festività',
      img: 'assets/images/icon_calendar/holiday.svg',
      id: 11,
      value: false
    },
    {
      label: 'Sprint review',
      img: 'assets/images/icon_calendar/sprint_review.svg',
      id: 9,
      value: false
    },
    {
      label: 'Permesso',
      img: 'assets/images/icon_calendar/permission.svg',
      id: 8,
      value: false
    },
    {
      label: 'Ferie',
      img: 'assets/images/icon_calendar/vacation.svg',
      id: 7,
      value: false
    },
    {
      label: 'Malattia',
      img: 'assets/images/icon_calendar/sickness.svg',
      id: 6,
      value: false
    },
    {
      label: 'Corso',
      img: 'assets/images/icon_calendar/course.svg',
      id: 10,
      value: false
    },
    {
      label: 'Review',
      img: 'assets/images/icon_calendar/review.svg',
      id: 5,
      value: false
    },
    {
      label: 'Remoto',
      img: 'assets/images/icon_calendar/smart.svg',
      id: 2,
      value: false
    }
  ];
  eventsSelected: string[] = [];

  destroy$: Subject<void> = new Subject();

  constructor() {
    super();
  }

  ngOnInit() {
    this.getTeams();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['widthColumn'] && this.first) {
      this.first = false;
    }
    this.animationState = this.isSideOpen ? 'open' : 'closed';
  }

  onOverlayClick() {
    this.animationState = 'closed';
  }

  onAnimationDone(event: any) {
    this.updateHeaderLabel();
    if (event.toState === 'closed') {
      this.closed.emit();
    }
  }

  /**
   * Questa funzione serve per formattare la label sul data picker, in maniera tale che vangato tornato
   * Mese anno
   * @returns string Mese anno. Es: Maggio 2025
   */
  updateHeaderLabel() {
    if (!this.datePicker) return;

    const headerEl = this.datePicker.nativeElement.querySelector(
      '.ant-picker-header-view'
    );

    if (headerEl) {
      const formatted = moment(this.monthSelected)
        .locale('it')
        .format('MMMM YYYY');
      headerEl.setAttribute(
        'data-label',
        formatted.charAt(0).toUpperCase() + formatted.slice(1)
      );
    }
  }

  get formattedMonth(): string {
    const text = moment(this.monthSelected).locale('it').format('MMMM YYYY');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   *
   * @param offset numero di mesi da aggiungere (es. -1 per il mese precedente, +1 per il successivo)
   */
  changeMonth(offset: number): void {
    const year = this.monthSelected.getFullYear();
    const month = this.monthSelected.getMonth();
    this.monthSelected = new Date(year, month + offset, 1);
    this.updateHeaderLabel();
  }

  /**
   * Funzione che serve per andare indietro di un mese
   */
  previousMonth(): void {
    this.changeMonth(-1);
    this.onPageChangeCalendar.emit(this.monthSelected);
  }

  /**
   * Funzione che serve per andare avanti di un mese
   */
  nextMonth(): void {
    this.changeMonth(1);
    this.onPageChangeCalendar.emit(this.monthSelected);
  }

  /**
   * Funzione che serve a ricavare le date di inizio e fine settimana (lunedì - venerdì) selezionata
   * @param {EventEmitter<{ Date, Date }>} dateEmitter date inizio e fine settimana (lavorativa)
   */
  changeRange() {
    this.updateHeaderLabel();
    const startOfWeek = moment(this.monthSelected).startOf('isoWeek').toDate();
    const endOfWeek = moment(this.monthSelected).isoWeekday(5).toDate();

    this.dateEmitter.emit({ startWeek: startOfWeek, endWeek: endOfWeek });
  }

  /**
   * Funzione per ricavare tutti i team di cui fa parte l'utente loggato
   * per admin vengono ritornati tutti i ruoli
   */
  getTeams() {
    this._teamService
      .getTeams()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          res.result.forEach((team: any) => {
            let obj = {
              label: team.alias,
              value: team.id
            };
            this.teams.push(obj);
          });
        }
      });
  }

  /**
   * Funzione che serve per capire quali team sono stati selezionati
   * per filtrare il calendario
   * @param {EventEmitter<number>} teamsEmitter id team selezionati
   */
  onTeamSelected() {
    this.teamsSelected = this.teams
      .filter((team: any) => team.checked)
      .map((team: any) => team.value);
    // emit dei team selezionati verso il componente calendar
    this.teamsEmitter.emit(this.teamsSelected);
  }

  /**
   * Funzione che serve per capire quali ruoli sono stati selezionati
   * per filtrare il calendario
   * @param {EventEmitter<number>} rolesEmitter id ruoli selezionati
   */
  onRoleSelected() {
    this.rolesSelected = this.roles
      .filter((role: any) => role.checked)
      .map((role: any) => role.value);
    //emit dei ruoli selezionati verso il componente calendar
    this.rolesEmitter.emit(this.rolesSelected);
  }

  /**
   * Funzione che serve per capire quali eventi sono stati selezionati
   * per filtrare il calendario
   * @param {EventEmitter<string>} eventsEmitter alias degli eventi selezionati
   */
  onEventSelected() {
    this.eventsSelected = this.events
      .filter((event: any) => event.value)
      .map((event: any) => event.id);
    //emit degli eventi selezionati verso il componente calendar
    this.eventsEmitter.emit(this.eventsSelected);
  }

  openModalDesiredSmart() {
    const userObj = {
      id: this.profileData.id,
      firstName: this.profileData.first_name,
      lastName: this.profileData.last_name,
      role: this.profileData.assignmentroles[0].alias,
      path: this.profileData.filestorage.path,
      maxSmart: this.profileData.max_smart_days
    };
    const modalRef = this._modalService.create({
      nzContent: ModalDesiredSmartComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '424px',
      nzClassName: 'custom-modal-desired-smart',
      nzComponentParams: {
        user: userObj,
        desiredSmart: this.profileData.desired_smart_days
      }
    });

    /**
     * Non appena chiudiamo la modale, se abbiamo settato i posti, andiamo a salvarli
     */
    modalRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event != null || event != undefined) {
          this._calendarSmartService
            .storeDesiredSmartDaysForUser(event)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {
                this.responseToast('success', res.message);
                this.profileData.desired_smart_days = event;
                // faccio un emit qualsiasi per richiamare la get di calendario
                this.teamsEmitter.emit(this.teamsSelected);
              },
              error: (err: any) => {
                this.responseToast('error', err.error.message);
              }
            });
        }
      });
  }

  /**
   * Metodo che serve per effettuare un emit al componente "calendar.component" per filtrare la visualizzazione
   * in base agli utenti che hanno effettuato una richiesta per una specifica settimana
   * @param usersId id degli utenti che hanno effettuato una richiesta
   * @param startWeek data in formato stringa
   */
  filterCalendarForUsersNotification(usersId: string[], date: string) {
    const startWeek = new Date(date);
    this.filterUsersrequest.emit({ usersId, startWeek });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
